import { useState, useEffect } from 'react';

const priceConfig = {
  service: {
    'coursier-moto': 15,
    utilitaire: 29,
    demenagement: 79,
    'livraison-express': 19,
  } as Record<string, number>,
  zone: {
    'paris-centre': 1,
    'paris-intramuros': 1.2,
    'banlieue-proche': 1.5,
    'ile-de-france': 2,
  } as Record<string, number>,
  weight: {
    leger: 1,
    moyen: 1.3,
    lourd: 1.8,
    'tres-lourd': 2.5,
  } as Record<string, number>,
  urgency: {
    standard: 1,
    'express-1h': 1.5,
    'ultra-express-30min': 2,
  } as Record<string, number>,
};

const MiniPriceSimulator = () => {
  const [service, setService] = useState('coursier-moto');
  const [zone, setZone] = useState('paris-centre');
  const [weight, setWeight] = useState('leger');
  const [urgency, setUrgency] = useState('standard');
  const [estimatedPrice, setEstimatedPrice] = useState(0);

  useEffect(() => {
    // Guard against missing config entries
    const base = priceConfig.service[service] ?? 0;
    const z = priceConfig.zone[zone] ?? 1;
    const w = priceConfig.weight[weight] ?? 1;
    const u = priceConfig.urgency[urgency] ?? 1;

    const price = Math.round(base * z * w * u);
    setEstimatedPrice(price);
  }, [service, zone, weight, urgency]);

  const handleWhatsApp = () => {
    try {
      const msg = `Bonjour, devis pour : ${service}, zone ${zone}, poids ${weight}, urgence ${urgency}. Estimé : ${estimatedPrice}€`;
      const url = `https://wa.me/33632410246?text=${encodeURIComponent(msg)}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to open WhatsApp link:', error);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 -mt-10 relative z-20">
      <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <i className="ri-calculator-line text-base text-white"></i>
          </div>
          <h3 className="text-base font-bold text-gray-900">
            Estimez votre prix en 10 secondes
          </h3>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Service
            </label>
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none cursor-pointer bg-gray-50"
            >
              <option value="coursier-moto">Coursier moto</option>
              <option value="utilitaire">Utilitaire</option>
              <option value="demenagement">Déménagement</option>
              <option value="livraison-express">Livraison express</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Zone
            </label>
            <select
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none cursor-pointer bg-gray-50"
            >
              <option value="paris-centre">Paris centre</option>
              <option value="paris-intramuros">Paris intra-muros</option>
              <option value="banlieue-proche">Banlieue proche</option>
              <option value="ile-de-france">Île-de-France</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Poids
            </label>
            <select
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none cursor-pointer bg-gray-50"
            >
              <option value="leger">Léger (&lt; 5kg)</option>
              <option value="moyen">Moyen (5-30kg)</option>
              <option value="lourd">Lourd (30-100kg)</option>
              <option value="tres-lourd">Très lourd (100kg+)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Urgence
            </label>
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none cursor-pointer bg-gray-50"
            >
              <option value="standard">Standard</option>
              <option value="express-1h">Express 1h</option>
              <option value="ultra-express-30min">
                Ultra-express 30min
              </option>
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-3">
          <div className="flex items-center gap-3 flex-1">
            <span className="text-sm text-gray-600 font-medium">
              Prix estimé :
            </span>
            <span className="text-3xl font-black text-teal-600">
              {estimatedPrice}€
            </span>
            <span className="text-xs text-gray-400">*indicatif</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:from-teal-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base"></i>
              Devis WhatsApp
            </button>
            <a
              href="tel:+33632410246"
              className="flex items-center gap-2 border-2 border-teal-500 text-teal-600 px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-base"></i>
              Appeler
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniPriceSimulator;
